/**
 * Frontend request deduplication utility
 * Prevents multiple identical requests from being made simultaneously
 */

type RequestKey = string;
type RequestPromise = Promise<any>;

const inFlightRequests = new Map<RequestKey, RequestPromise>();

/**
 * Deduplicate identical requests
 * If a request is already in-flight, return the existing promise
 * Otherwise, execute the request and cache it
 */
export function deduplicateRequest<T>(
  key: RequestKey,
  executor: () => Promise<T>,
  timeoutMs = 30000
): Promise<T> {
  // Check if request is already in-flight
  if (inFlightRequests.has(key)) {
    console.log(`🔄 Reusing in-flight request for key: ${key}`);
    return inFlightRequests.get(key)! as Promise<T>;
  }

  console.log(`🚀 Starting new deduped request for key: ${key}`);

  // Create the request promise with timeout
  let resolvedValue: T;
  const promise = Promise.race([
    executor(),
    new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(new Error(`Request timeout after ${timeoutMs}ms`)),
        timeoutMs
      )
    ),
  ])
    .then(value => {
      resolvedValue = value;
      return value as T;
    })
    .finally(() => {
      // Clean up the cached promise when done
      inFlightRequests.delete(key);
    }) as Promise<T>;

  // Cache the promise
  inFlightRequests.set(key, promise);
  return promise as Promise<T>;
}

/**
 * Clear all in-flight requests (useful for testing)
 */
export function clearAllInFlightRequests() {
  inFlightRequests.clear();
}

/**
 * Get the number of in-flight requests
 */
export function getInFlightRequestCount(): number {
  return inFlightRequests.size;
}
